import { useRef, useMemo, useCallback } from 'react'
import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  Title,
} from '..'
import { Button } from '../../../../../components/buttons'
import { intl } from '../../../../../../i18n'
import TextInput from '../../../../../components/editors/input/TextInput'
import { styled, CircularProgress, Tooltip } from '@mui/material'
import { colorPalette } from '../../../../../style/colorPalette'
import {
  usePageEntityMap,
  PageTypeLabel,
  PAGE_TYPE_LABELS,
} from './hooks/usePageEntityMap'
import { PageConfigForm } from './components/PageConfigForm'
import { PageThumbnailList } from './components/PageThumbnailList'
import { AddPageMenu } from './components/AddPageMenu'
import { useTemplateDialog } from './hooks/useTemplateDialog'
import {
  validatePageEntity,
  validateTemplateName,
} from '../../../../../../domain/entity/version-report/VersionReportTemplateEntity/validator'
import { CacheProvider } from '../context/CacheContext'
import {
  VersionReportTemplateEntity,
  VersionReportTemplatePageEntity,
} from '../../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { VersionReportTemplatePageFormModel } from './model/VersionReportTemplatePageFormModel'
import { generateUuid } from '../../../../../../utils/uuids'

const PAGE_TYPE_LABEL_VALUES: PageTypeLabel[] = Object.values(PAGE_TYPE_LABELS)

type TemplateDialogProps = {
  projectUuid: string
  open: boolean
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  original?: VersionReportTemplateEntity
}

export const TemplateDialog = ({
  projectUuid,
  open,
  onAfterSubmit,
  onClose,
  original,
}: TemplateDialogProps) => {
  const thumbnailScrollRef = useRef<HTMLDivElement>(null)

  const {
    pageEntityMap,
    currentPageEntity,
    currentSelectedPageNumber,
    setCurrentSelectedPageNumber,
    addPageEntity,
    updatePageEntity,
    deletePageEntity,
    reorderPages,
    resetPageEntityMap,
  } = usePageEntityMap(original?.pages)

  const {
    templateName,
    setTemplateName,
    loading,
    handleSubmit: submitTemplate,
    handleClose,
  } = useTemplateDialog({
    onAfterSubmit,
    onClose,
    resetPageEntityMap,
    original,
  })

  const sortedPages = useMemo(
    () =>
      Array.from(pageEntityMap.values()).sort(
        (a, b) => a.pageNumber - b.pageNumber
      ),
    [pageEntityMap]
  )

  const templateNameErrors = useMemo(
    () => validateTemplateName(templateName),
    [templateName]
  )

  // TODO: fix validation logic.
  const handleFormModelAsEntityForValidation = useCallback(
    (page: VersionReportTemplatePageFormModel) => {
      return {
        uuid: generateUuid(),
        ...page,
      } as VersionReportTemplatePageEntity
    },
    []
  )

  const pageValidationMap = useMemo(() => {
    const map = new Map<number, any>()
    sortedPages.forEach(page => {
      const errors = validatePageEntity(
        handleFormModelAsEntityForValidation(page)
      )
      if (errors.length > 0) {
        map.set(page.pageNumber, errors)
      }
    })
    return map
  }, [handleFormModelAsEntityForValidation, sortedPages])

  const pageErrorMap = useMemo(() => {
    const map = new Map<number, boolean>()
    sortedPages.forEach(page => {
      const errors = validatePageEntity(
        handleFormModelAsEntityForValidation(page)
      )
      map.set(page.pageNumber, errors.length > 0)
    })
    return map
  }, [handleFormModelAsEntityForValidation, sortedPages])

  const hasAnyValidationError = useMemo(
    () => templateNameErrors.length > 0 || pageValidationMap.size > 0,
    [templateNameErrors, pageValidationMap]
  )

  const handleSubmit = () => {
    submitTemplate(sortedPages)
  }

  const handlePageTypeSelect = useCallback(
    (pageTypeLabel: PageTypeLabel) => {
      addPageEntity(pageTypeLabel)
      setTimeout(() => {
        thumbnailScrollRef.current?.scrollTo({
          top: thumbnailScrollRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }, 0)
    },
    [addPageEntity]
  )

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth={true}
      PaperProps={{
        style: {
          minHeight: '550px',
          maxHeight: '80vh',
        },
      }}
    >
      {open && (
        <CacheProvider projectUuid={projectUuid}>
          <DialogTitleContainer>
            <TemplateTitleText>
              {original ? 'テンプレートの編集' : 'テンプレートの新規作成'}
            </TemplateTitleText>
            <TemplateNameInput
              label="テンプレート名"
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              required={true}
              disabled={loading}
              placeholder="テンプレートの名前を入力してください"
              variant="outlined"
              error={templateNameErrors.length > 0}
              helperText={templateNameErrors[0] || ''}
            />
          </DialogTitleContainer>
          <DialogContentContainer>
            <LeftPanel>
              <ThumbnailScrollArea ref={thumbnailScrollRef}>
                <PageThumbnailList
                  pages={sortedPages}
                  selectedPageNumber={currentSelectedPageNumber}
                  pageErrorMap={pageErrorMap}
                  onSelectPage={setCurrentSelectedPageNumber}
                  onDeletePage={deletePageEntity}
                  onReorderPages={reorderPages}
                />
              </ThumbnailScrollArea>

              <AddPageMenu
                pageTypeLabels={PAGE_TYPE_LABEL_VALUES}
                onPageTypeSelect={handlePageTypeSelect}
                disabled={loading}
              />
            </LeftPanel>
            <RightPanel>
              {currentPageEntity ? (
                <PageConfigForm
                  page={currentPageEntity}
                  projectUuid={projectUuid}
                  validationErrors={
                    currentSelectedPageNumber
                      ? pageValidationMap.get(currentSelectedPageNumber)
                      : undefined
                  }
                  onUpdate={updatePageEntity}
                />
              ) : (
                <EmptyState>
                  <EmptyStateText>
                    左側のメニューからページタイプを選択してください
                  </EmptyStateText>
                </EmptyState>
              )}
            </RightPanel>
          </DialogContentContainer>
          <DialogActionsContainer>
            <Tooltip
              title={
                hasAnyValidationError
                  ? 'エラーのあるページを修正してください'
                  : ''
              }
              arrow={true}
            >
              <SubmitButtonContainer>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    loading || sortedPages.length === 0 || hasAnyValidationError
                  }
                  colorPattern="skyBlue"
                >
                  {loading
                    ? '保存中...'
                    : original
                      ? `テンプレートを更新する (${sortedPages.length}ページ)`
                      : `テンプレートを保存する (${sortedPages.length}ページ)`}
                </Button>
                {loading && <LoadingSpinner size={20} />}
              </SubmitButtonContainer>
            </Tooltip>
            <Button
              onClick={handleClose}
              colorPattern="monotone"
              variant="outlined"
              disabled={loading}
            >
              {intl.formatMessage({ id: 'dialog.cancel' })}
            </Button>
          </DialogActionsContainer>
        </CacheProvider>
      )}
    </StyledDialog>
  )
}

const DialogTitleContainer = styled(StyledDialogTitle)({
  alignItems: 'flex-start',
  gap: '12px',
  marginTop: '-45px',
  marginLeft: '-24px',
})

const DialogContentContainer = styled(StyledDialogContent)({
  alignItems: 'flex-start',
  border: `1px solid ${colorPalette.monotone[2]}`,
  width: 'calc(100%)',
  display: 'flex',
  flexDirection: 'row',
  padding: 0,
  height: '400px',
})

const LeftPanel = styled('div')({
  width: '30%',
  backgroundColor: colorPalette.monotone[1],
  borderRight: `1px solid ${colorPalette.monotone[2]}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'hidden',
})

const ThumbnailScrollArea = styled('div')({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
})

const RightPanel = styled('div')({
  width: '70%',
  height: '100%',
  overflowY: 'auto',
})

const DialogActionsContainer = styled(StyledDialogActions)({
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '10px',
  marginBottom: '-45px',
})

const TemplateTitleText = styled(Title)({
  textAlign: 'left',
})

const TemplateNameInput = styled(TextInput)({
  width: '50%',
  maxWidth: '400px',
  marginTop: '8px',
})

const EmptyState = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: colorPalette.monotone[1],
})

const EmptyStateText = styled('span')({
  fontSize: '16px',
  color: colorPalette.monotone[4],
  textAlign: 'center',
})

const SubmitButtonContainer = styled('div')({
  position: 'relative',
  display: 'inline-block',
})

const LoadingSpinner = styled(CircularProgress)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: '-10px',
  marginLeft: '-10px',
  color: colorPalette.monotone[0],
})
